export const IMPACT_LEVELS = ['Minor', 'Normal', 'Significant', 'Major', 'Policy'];

const levelRank = IMPACT_LEVELS.reduce((acc, level, index) => ({ ...acc, [level]: index }), {});

export const IMPACT_POLICIES = {
  Minor: {
    level: 'Minor',
    notice: '24–72 hours',
    deadlineDays: 3,
    participationThresholdPercent: 0,
    approvalThresholdPercent: 50,
    approvalBasis: 'Consent unless objected; yes votes must outnumber no votes.',
    minimumYesVotes: 3,
    summary: 'Small, optional, reversible ideas that do not bind the whole group.',
  },
  Normal: {
    level: 'Normal',
    notice: '3–7 days',
    deadlineDays: 7,
    participationThresholdPercent: 10,
    approvalThresholdPercent: 51,
    approvalBasis: '51% yes among voters after at least 10% participation.',
    minimumYesVotes: null,
    summary: 'Ordinary group life where interested members can participate without binding everyone heavily.',
  },
  Significant: {
    level: 'Significant',
    notice: '7–14 days',
    deadlineDays: 14,
    participationThresholdPercent: 25,
    approvalThresholdPercent: 60,
    approvalBasis: '60% yes among voters after at least 25% participation.',
    minimumYesVotes: null,
    summary: 'Public, funded, coordinated, or multi-person ideas that deserve stronger notice.',
  },
  Major: {
    level: 'Major',
    notice: '14–30 days',
    deadlineDays: 30,
    participationThresholdPercent: 40,
    approvalThresholdPercent: 67,
    approvalBasis: '67% yes among voters after at least 40% participation; the largest decisions may require 51% of all members voting yes.',
    minimumYesVotes: null,
    summary: 'Decisions with serious money, safety, legal, reputational, or whole-group consequences.',
  },
  Policy: {
    level: 'Policy',
    notice: '14–45 days',
    deadlineDays: 45,
    participationThresholdPercent: 51,
    approvalThresholdPercent: 51,
    approvalBasis: '51% yes from the entire eligible group at the time.',
    minimumYesVotes: null,
    requiresWholeGroupYes: true,
    summary: 'Standing rules, membership standards, authority, treasury rules, doctrine, or group coordination.',
  },
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const normalizeArray = (value) => {
  if (!value) {
    return [];
  }

  return Array.isArray(value) ? value : [value];
};

const highest = (current, next) => (levelRank[next] > levelRank[current] ? next : current);

const addTrigger = (state, level, reason) => {
  state.level = highest(state.level, level);
  state.reasons.push(reason);
};

export function evaluateIdeaImpact(answers = {}) {
  const state = {
    level: 'Minor',
    reasons: [],
  };

  const type = answers.campaignType || answers.ideaType || '';
  const treasuryAmount = toNumber(answers.groupFundsAmount);
  const treasuryPercent = toNumber(answers.groupFundsPercent);
  const separateRaiseAmount = toNumber(answers.separateRaiseAmount);
  const volunteers = toNumber(answers.volunteerCount);
  const affectedScope = answers.affectedScope || '';
  const duration = answers.duration || '';
  const ideaShape = answers.ideaShape || answers.ideaSubtype || '';
  const fundingPlan = answers.fundingPlan || '';
  const requiredRoles = normalizeArray(answers.requiredRoles);
  const riskFlags = normalizeArray(answers.riskFlags);
  const resourceFlags = normalizeArray(answers.resourceFlags);

  if (type === 'policy' || answers.changesPolicy) {
    addTrigger(state, 'Policy', 'It creates or changes a standing policy, membership rule, authority, treasury rule, doctrine, or coordination process.');
  }

  if (['gift', 'position'].includes(type)) {
    addTrigger(state, 'Significant', 'This type affects shared resources, formal roles, or group commitments.');
  }

  if (['purchase', 'equipment', 'inventory'].includes(ideaShape) || answers.inventoryAcquisitionRequested) {
    addTrigger(state, 'Significant', 'It may create a group-owned item that needs stewardship, storage, and later inventory tracking.');
  }

  if (['fundraiser', 'externalGift', 'well', 'internationalMercy'].includes(ideaShape) || fundingPlan === 'separate' || fundingPlan === 'mixed') {
    addTrigger(state, 'Significant', 'It depends on raising or stewarding designated gifts, so the group needs clear reporting.');
  }

  if (['institution', 'school', 'ongoingMinistry'].includes(ideaShape)) {
    addTrigger(state, 'Major', 'It points toward an institution or durable ministry that should be tested through serious planning and stages.');
  }

  if (['crisisAction', 'rescue', 'intervention'].includes(ideaShape)) {
    addTrigger(state, 'Major', 'It calls for courageous action in a dangerous setting and needs serious planning, local wisdom, and protection for everyone involved.');
  }

  if (type === 'calendarEvent') {
    addTrigger(state, 'Normal', 'It creates a scheduled group event or activity.');
  }

  if (type === 'action') {
    addTrigger(state, 'Normal', 'It asks the group to take action beyond a simple conversation.');
  }

  if (treasuryAmount > 0 || answers.usesGroupFunds) {
    addTrigger(state, 'Normal', 'It uses or requests group treasury funds.');
  }

  if (treasuryAmount >= 250 || treasuryPercent >= 2) {
    addTrigger(state, 'Significant', 'The requested group funds are meaningful enough to require stronger participation.');
  }

  if (treasuryAmount >= 2500 || treasuryPercent >= 10) {
    addTrigger(state, 'Major', 'The requested group funds are large relative to normal group stewardship.');
  }

  if (treasuryPercent >= 25) {
    addTrigger(state, 'Major', 'It could use a very large share of group funds and may require whole-group yes support.');
  }

  if (answers.raisesSeparateFunds || separateRaiseAmount > 0) {
    addTrigger(state, 'Normal', 'It raises money separately from the treasury and still needs transparent stewardship.');
  }

  if (answers.publicFundraising && separateRaiseAmount >= 250) {
    addTrigger(state, 'Significant', 'It raises a meaningful amount in connection with the group and needs visible stewardship.');
  }

  if (separateRaiseAmount >= 2500) {
    addTrigger(state, 'Major', 'It raises a large amount that could affect trust, reputation, and stewardship.');
  }

  if (volunteers >= 3) {
    addTrigger(state, 'Normal', 'It needs several volunteers.');
  }

  if (volunteers >= 8) {
    addTrigger(state, 'Significant', 'It needs a larger volunteer team or real coordination.');
  }

  if (volunteers >= 20) {
    addTrigger(state, 'Major', 'It needs a large volunteer effort or recurring service burden.');
  }

  if (requiredRoles.length > 0) {
    addTrigger(state, 'Significant', 'It requires defined roles rather than only optional helpers.');
  }

  if (requiredRoles.some((role) => ['treasurer', 'childcare', 'safety', 'transportation', 'spokesperson', 'legal'].includes(role))) {
    addTrigger(state, 'Major', 'It requires a sensitive role such as treasurer, childcare, safety, transportation, spokesperson, or legal steward.');
  }

  if (answers.usesGroupNamePublicly || answers.publicRepresentation || resourceFlags.includes('public')) {
    addTrigger(state, 'Normal', 'It is visible outside the group, so the proposal should be clear about who is acting and why.');
  }

  if (affectedScope === 'whole-group' || affectedScope === 'public' || affectedScope === 'multi-group') {
    addTrigger(state, affectedScope === 'whole-group' ? 'Significant' : 'Significant', 'It affects the whole group, outside neighbors, or another group.');
  }

  if (duration === 'ongoing' || duration === 'permanent') {
    addTrigger(state, duration === 'permanent' ? 'Major' : 'Significant', 'It creates an ongoing or durable commitment.');
  }

  if (riskFlags.length > 0) {
    addTrigger(state, 'Normal', 'It includes practical concerns the group should notice instead of discovering late.');
  }

  if (riskFlags.some((risk) => ['legal', 'privacy', 'travel', 'safety', 'property', 'debt'].includes(risk))) {
    addTrigger(state, 'Significant', 'It includes safety, legal, travel, property, privacy, debt, or similar planning responsibilities.');
  }

  if (riskFlags.some((risk) => ['children', 'vulnerable'].includes(risk))) {
    addTrigger(state, 'Normal', 'It involves care for children or vulnerable people, so the group should plan respectfully without treating mercy as suspicious.');
  }

  if (riskFlags.includes('danger') || riskFlags.includes('law-enforcement') || riskFlags.includes('trafficking')) {
    addTrigger(state, 'Major', 'It may confront active danger or exploitation and needs a serious protection plan.');
  }

  if (!state.reasons.length) {
    state.reasons.push('It appears optional, low-cost, reversible, and limited to people who choose to participate.');
  }

  const policy = IMPACT_POLICIES[state.level];

  return {
    ...policy,
    reasons: Array.from(new Set(state.reasons)),
    thresholdSummary: policy.requiresWholeGroupYes
      ? `${policy.approvalThresholdPercent}% yes from all eligible members at the time.`
      : `${policy.participationThresholdPercent}% participation and ${policy.approvalThresholdPercent}% yes among voters.`,
  };
}

export function calculateVoteRequirements(impactLevel, eligibleMembers = 0) {
  const policy = IMPACT_POLICIES[impactLevel] || IMPACT_POLICIES.Normal;
  const eligible = Math.max(0, Number(eligibleMembers) || 0);
  const minimumVoters = policy.requiresWholeGroupYes
    ? Math.ceil(eligible * (policy.participationThresholdPercent / 100))
    : Math.ceil(eligible * (policy.participationThresholdPercent / 100));
  const yesVotesNeeded = policy.requiresWholeGroupYes
    ? Math.ceil(eligible * (policy.approvalThresholdPercent / 100))
    : Math.max(policy.minimumYesVotes || 0, Math.ceil(minimumVoters * (policy.approvalThresholdPercent / 100)));

  return {
    eligibleMembers: eligible,
    minimumVoters,
    yesVotesNeeded,
    policy,
  };
}
